/**
 * Tiny typed fetch wrapper. Same-origin: relative paths to /api/* are
 * proxied to the Worker in dev and routed to the Worker in prod.
 */
export class ApiError extends Error {
  status: number;
  data: unknown;
  constructor(status: number, data: unknown) {
    // Prefer the diagnostic `detail` field (added on 500 responses)
    // when present, falling back to `error` for known-shape failures.
    // Makes upload errors land in the UI as the actual SQL / runtime
    // message instead of an opaque "internal".
    let message = `HTTP ${status}`;
    if (typeof data === 'object' && data !== null) {
      const obj = data as { error?: unknown; detail?: unknown };
      if (typeof obj.detail === 'string' && obj.detail.length > 0) {
        message = obj.detail;
      } else if (obj.error !== undefined) {
        message = String(obj.error);
      }
    }
    super(message);
    this.status = status;
    this.data = data;
  }
}

export interface RequestOpts {
  /** Override the default 6s abort timeout. Use a longer value for
   *  endpoints that do real work server-side (e.g. the AI-backed
   *  /videos/auto-tag pass), which would otherwise be killed mid-flight
   *  and surface as a spurious failure even though the Worker finished. */
  timeoutMs?: number;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  opts?: RequestOpts,
): Promise<T> {
  // Default 6-second timeout guards against hanging when the API is
  // unreachable (e.g. proxy target down or DNS failure on the _redirects
  // target). Callers doing heavier server work can pass a longer one.
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), opts?.timeoutMs ?? 6000);
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method,
      credentials: 'include',
      headers: body ? { 'content-type': 'application/json' } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
  } catch (e) {
    if (controller.signal.aborted) {
      throw new Error('Request timed out — the server may still be working.');
    }
    throw e;
  } finally {
    clearTimeout(timeout);
  }
  const text = await res.text();
  const data = text ? safeParse(text) : null;
  if (!res.ok) throw new ApiError(res.status, data);
  return data as T;
}

function safeParse(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return s;
  }
}

export const api = {
  get: <T>(path: string, opts?: RequestOpts) => request<T>('GET', path, undefined, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOpts) =>
    request<T>('POST', path, body, opts),
  put: <T>(path: string, body?: unknown, opts?: RequestOpts) =>
    request<T>('PUT', path, body, opts),
  patch: <T>(path: string, body?: unknown, opts?: RequestOpts) =>
    request<T>('PATCH', path, body, opts),
  delete: <T>(path: string, opts?: RequestOpts) =>
    request<T>('DELETE', path, undefined, opts),
};

/**
 * Stable per-photo URL for an <img src=...>. The Worker streams the R2
 * object with `Cache-Control: immutable`, so the browser + service
 * worker both cache by this path. (The presigned R2 URLs that appear
 * in API responses change every request — different signature, new
 * cache entry — and are kept only for backwards compatibility.)
 */
export function photoBytesUrl(
  id: string,
  variant: 'thumb' | 'large' | 'original',
): string {
  return `/api/photos/${encodeURIComponent(id)}/${variant}`;
}

export interface Me {
  member: {
    id: string;
    familyId: string;
    displayName: string;
    role: 'parent' | 'youth';
    translation: string;
    voices: string[];
    notifyTime: string | null;
    schoolDnd: boolean;
  };
  family: {
    id: string;
    name: string;
    inviteCode: string;
    timezone: string;
  };
  members: {
    id: string;
    displayName: string;
    role: 'parent' | 'youth';
    birthdate: string | null;
  }[];
  /** True only for the instance owner (Jared) — the first member ever
   *  created on the deployment. Gates the Admin page, instance ops, and
   *  all circle administration (create / invite families / edit). */
  isOwner: boolean;
  /** True when the caller is their own household's lead (earliest
   *  member.created_at within their family). Gates the /household page
   *  where they manage their own members + settings. Jared is both. */
  isFamilyOwner: boolean;
  /** Phase 1 auth refactor — surfaces what the Settings → Account
   *  section needs without N extra round trips. `email` is null until
   *  the user enrolls; `emailVerified` flips true after they enter
   *  the OTP. `passkeyCount` is informational today (passkey sign-in
   *  itself lands in Phase 2). */
  account: {
    email: string | null;
    emailVerified: boolean;
    passkeyCount: number;
  };
}

export interface SessionListItem {
  id: string;
  label: string | null;
  createdAt: number;
  lastSeenAt: number;
  /** True for the session that authenticated the current request. */
  current: boolean;
}

export interface TodayResponse {
  date: string;
  verse: {
    ref: string;
    translation: string;
    body: string;
    principle: string;
    application: string;
    applicationAdult: string;
    applicationYouth: string;
    discussion: string;
    audioUrl: string | null;
    planSlug: string;
    planDay: number;
  };
  fotf: {
    kicker: string;
    title: string;
    url: string | null;
    duration: string | null;
    host: string | null;
  } | null;
}

export interface Prayer {
  id: string;
  familyId: string;
  authorId: string;
  body: string;
  status: 'active' | 'answered' | 'archived';
  answeredAt: number | null;
  answeredNote: string | null;
  createdAt: number;
}

export interface SermonNote {
  id: string;
  memberId: string;
  authorName: string;
  date: string; // YYYY-MM-DD (the Sunday)
  sermonTitle: string | null;
  sermonUrl: string | null;
  body: string; // markdown
  visibility: 'private' | 'family';
  photoIds: string[];
  /** True when the caller authored this note (gates edit/delete). */
  mine: boolean;
  createdAt: number;
  updatedAt: number;
}

/** Latest sermon scraped weekly into KV — prefills the composer header. */
export interface LatestSermon {
  title: string;
  url: string;
  fetchedAt: number;
}

export interface SermonNotesResponse {
  notes: SermonNote[];
  latestSermon: LatestSermon | null;
}

export interface OnThisDayPhoto {
  id: string;
  url: string;
  thumbUrl: string;
  takenAt: number | null;
  caption: string | null;
}

export type MemoryVerseMode =
  | 'word_bank'
  | 'first_letters'
  | 'from_memory'
  | 'timed_recall';

export interface MemoryVerseThisWeek {
  weekStart: string;
  verse: { date: string; ref: string; body: string } | null;
  me: {
    bestMode: MemoryVerseMode | null;
    bestScore: number;
    attempts: number;
    completedAt: number | null;
    streak: number;
  };
  family: {
    memberId: string;
    displayName: string;
    bestScore: number;
    completedAt: number | null;
  }[];
}

export interface MemoryVerseAttemptResponse {
  weekStart: string;
  me: MemoryVerseThisWeek['me'];
}
